import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { requireGlobalCapability } from '@/lib/route-guards';
import { searchEnum } from '@/lib/search-enum';
import { warm } from '@/api/loaders';
import { adminRemediations } from '@/api/resources';
import { RemediationsView } from '@/screens/admin-remediations';
import { REMEDIATION_ACTIONS } from '@/lib/remediation-view';

/**
 * Which repair, in the URL.
 *
 * Not `useState`: a gate that only exists after the component mounts is
 * unreachable from a loader, so every switch would be a cold fetch onto an
 * empty body — and an operator could not send a colleague a link to the thing
 * they are looking at.
 */
const action = searchEnum(
  'action',
  REMEDIATION_ACTIONS,
  'reset_retry_budget',
);

export const Route = createFileRoute('/admin/remediations')({
  beforeLoad: requireGlobalCapability('config_manage', 'full'),
  validateSearch: (search: Record<string, unknown>) => ({
    action: action.parse(search),
    // The selected entity is a candidate key, not a closed set, so it is
    // carried as-is and validated against the fetched list rather than here.
    entity: typeof search.entity === 'string' ? search.entity : undefined,
  }),
  loader: ({ context }) => warm(context.queryClient, adminRemediations()),
  component: RemediationsRoute,
});

function RemediationsRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <RemediationsView
      action={search.action}
      entity={search.entity}
      onNavigate={(next: { action: string; entity?: string }) =>
        void navigate({ search: next as never, replace: true })
      }
    />
  );
}
